import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useParams, Navigate } from "react-router-dom";
import DomainHiddenSeoContent from "../../components/agentic/DomainHiddenSeoContent";
import { renderUseCaseFlow } from "../../components/useCases/renderUseCaseFlow";
import {
  SITE_BASE_URL,
  buildFaqJsonLdFromFaqs,
  buildUseCaseBreadcrumbJsonLd,
  buildUseCaseServiceJsonLd,
} from "../../data/agenticSeoUtils";
import {
  absoluteAssetUrl,
  generateUseCaseJsonLd,
  buildBreadcrumbJsonLd as buildFinancialBreadcrumbJsonLd,
} from "../../data/financialUseCases";
import {
  basePathForMode,
  pageModeFromPath,
  resolveUseCaseDomain,
} from "../../data/useCasePageRegistry";

const PAGE_STYLES = `
  .uc-detail-page { background: #f5f8fc; min-height: 100vh; }
  .uc-detail-main { max-width: 960px; margin: 0 auto; padding: 32px 24px 64px; }
  .uc-detail-breadcrumb ol { display: flex; flex-wrap: wrap; gap: 8px; list-style: none; padding: 0; margin: 0 0 24px; font-size: 14px; color: #5d7493; }
  .uc-detail-breadcrumb a { color: #1d3f7d; text-decoration: none; }
  .uc-detail-breadcrumb a:hover { text-decoration: underline; }
  .uc-detail-eyebrow { color: #1d3f7d; font-weight: 700; text-transform: uppercase; font-size: 12px; letter-spacing: 0.06em; margin: 0 0 8px; }
  .uc-detail-title { font-family: Exo, sans-serif; color: #0e2440; font-size: 34px; line-height: 1.2; margin: 0 0 14px; }
  .uc-detail-summary { font-size: 17px; line-height: 1.7; color: #2a3a4c; margin: 0 0 24px; }
  .uc-detail-section h2 { font-family: Exo, sans-serif; font-size: 22px; margin: 0 0 12px; }
  .uc-detail-section p { font-size: 16px; line-height: 1.75; color: #2a3a4c; margin: 0; }
  .uc-detail-section { margin-bottom: 28px; }
  .uc-detail-flow { margin: 28px 0; background: #fff; border-radius: 12px; padding: 18px; border: 1px solid #dbe7f7; }
  .uc-detail-hero-image { width: 100%; height: auto; border-radius: 12px; margin: 28px 0; background: #fff; display: block; }
`;

export default function DomainUseCasePage() {
  const { domainSlug, groupId, useCaseId } = useParams();
  const location = useLocation();
  const mode = pageModeFromPath(location.pathname);
  const basePath = basePathForMode(mode);
  const bundle = resolveUseCaseDomain(mode, domainSlug);

  if (!bundle) {
    return <Navigate to={basePath} replace />;
  }

  const group = bundle.groups.find((g) => g.id === groupId);
  const useCases = bundle.useCasesByGroup[groupId] || [];
  const uc = useCases.find((item) => item.id === useCaseId);

  if (!group || !uc) {
    return (
      <Navigate
        to={group ? `${basePath}/${domainSlug}/${groupId}` : `${basePath}/${domainSlug}`}
        replace
      />
    );
  }

  const isFinancialAgentic = mode === "agentic" && domainSlug === "financial-domain";
  const serviceJsonLd = isFinancialAgentic
    ? generateUseCaseJsonLd(uc)
    : buildUseCaseServiceJsonLd(uc, mode);
  const breadcrumbJsonLd = isFinancialAgentic
    ? buildFinancialBreadcrumbJsonLd(uc)
    : buildUseCaseBreadcrumbJsonLd({ mode, domainSlug, domainIntro: bundle.intro, group, useCase: uc });
  const faqJsonLd = uc.faqs?.length ? buildFaqJsonLdFromFaqs(uc.faqs) : null;
  const ogImage = uc.image ? absoluteAssetUrl(uc.image) : `${SITE_BASE_URL}/logo.png`;
  const automationLabel = mode === "rpa" ? "RPA" : "Agentic AI";
  const metaTitle = uc.metaTitle || `${uc.title} | ${uc.subIndustry} ${automationLabel}`;
  const metaDescription = uc.metaDescription || uc.solution;
  const keywords = uc.keywordsArray?.join(", ") || uc.seoKeywords || "";
  const flow = renderUseCaseFlow(mode, domainSlug, uc);

  return (
    <div className="uc-detail-page">
      <Helmet>
        <html lang="en" />
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        {keywords ? <meta name="keywords" content={keywords} /> : null}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta name="author" content="Surens Inffotek" />
        <meta name="abstract" content={uc.seoSummary || uc.summary} />
        <link rel="canonical" href={uc.canonicalUrl} />
        <link rel="alternate" type="text/plain" title="LLM summary" href={`${SITE_BASE_URL}/llms.txt`} />
        <link rel="alternate" type="text/plain" title="LLM agentic catalog" href={`${SITE_BASE_URL}/llms-full.txt`} />
        <link rel="alternate" type="text/plain" title="LLM RPA catalog" href={`${SITE_BASE_URL}/llms-rpa.txt`} />
        <meta property="og:type" content="article" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="Surens Inffotek" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={uc.canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        {uc.imageAlt ? <meta property="og:image:alt" content={uc.imageAlt} /> : null}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{JSON.stringify(serviceJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        {faqJsonLd ? <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script> : null}
      </Helmet>
      <style>{PAGE_STYLES}</style>


      <main className="uc-detail-main">
        <nav aria-label="Breadcrumb" className="uc-detail-breadcrumb">
          <ol>
            <li><Link to="/">Home</Link></li>
            <li aria-hidden="true">/</li>
            <li><Link to={basePath}>{mode === "rpa" ? "RPA Use Cases" : "Agentic AI"}</Link></li>
            <li aria-hidden="true">/</li>
            <li><Link to={`${basePath}/${domainSlug}`}>{bundle.intro.title}</Link></li>
            <li aria-hidden="true">/</li>
            <li><Link to={`${basePath}/${domainSlug}/${group.id}`}>{group.label}</Link></li>
            <li aria-hidden="true">/</li>
            <li aria-current="page">{uc.title}</li>
          </ol>
        </nav>

        <article itemScope itemType="https://schema.org/TechArticle">
          <header>
            <p className="uc-detail-eyebrow">
              {group.label} · {automationLabel} Automation
            </p>
            <h1 className="uc-detail-title" itemProp="headline">{uc.title}</h1>
            {(uc.seoSummary || uc.summary) ? (
              <p className="uc-detail-summary" itemProp="description">{uc.seoSummary || uc.summary}</p>
            ) : null}
          </header>

          {uc.image && isFinancialAgentic ? (
            <img
              src={uc.image}
              alt={uc.imageAlt || uc.title}
              loading="lazy"
              width="640"
              height="400"
              className="uc-detail-hero-image"
              itemProp="image"
            />
          ) : null}

          <section className="uc-detail-section" aria-labelledby="challenge-heading">
            <h2 id="challenge-heading" style={{ color: "#1d3f7d" }}>The Challenge</h2>
            <p itemProp="about">{uc.challenge}</p>
          </section>

          <section className="uc-detail-section" aria-labelledby="solution-heading">
            <h2 id="solution-heading" style={{ color: "#178f80" }}>
              {mode === "rpa" ? "The RPA Solution" : "The Agentic AI Solution"}
            </h2>
            <p itemProp="text">{uc.solution}</p>
          </section>

          {flow ? <div className="uc-detail-flow">{flow}</div> : null}
          <link itemProp="url" href={uc.canonicalUrl} />
        </article>
      </main>

      <DomainHiddenSeoContent domainTitle={uc.title} useCases={[uc]} />
    </div>
  );
}

